import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Globe, Tag, User, Calendar } from "lucide-react";
import { ButtonOutline } from "@/components/common/StandardButton";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { TableContainer } from "@/components/common/TableContainer";
import { Seller, getSellerById } from "@/services/sellerService";
import { format } from "date-fns";

export default function SellerDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const sellerId = params?.sellerId as string;
  const storeType = params?.storeType as string;
  const storeId = params?.storeId as string;

  const [seller, setSeller] = useState<Seller | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSeller = async () => {
      if (!sellerId) {
        setError("Seller ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getSellerById(sellerId);
        setSeller(response.data);
      } catch (err: any) {
        setError(err?.message || "Failed to load seller details");
      } finally {
        setLoading(false);
      }
    };

    fetchSeller();
  }, [sellerId]);

  const handleBackClick = () => {
    navigate(`/${storeType}/${storeId}/sellers`);
  };

  const breadcrumbItems = [
    { label: "Sellers", href: `/${storeType}/${storeId}/sellers` },
    { label: seller?.name || "Loading...", isActive: true },
  ];

  const InformationCard = ({ label, value, icon: Icon }: { label: string; value: string | null | undefined; icon: any }) => (
    <div className="flex items-start gap-3">
      <Icon className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <p className="text-sm font-semibold text-foreground">{value || "-"}</p>
      </div>
    </div>
  );

  return (
    <div className="view min-h-screen bg-background gap-2.5 flex flex-col">
      <Breadcrumb items={breadcrumbItems} showBackButton={false} />

      {/* Header */}
      <div className="border-b border-border bg-card rounded-lg">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <ButtonOutline onClick={handleBackClick} className="flex items-center gap-2 px-3">
              <ArrowLeft className="h-4 w-4" />
              Back
            </ButtonOutline>

            <div>
              <h1 className="text-lg font-semibold text-foreground">{seller?.name || "Seller"}</h1>
              <p className="text-sm text-muted-foreground">Seller #{sellerId?.substring(0, 12)}</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border border-border border-t-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading seller details...</p>
          </div>
        </div>
      ) : seller ? (
        <div className="flex gap-2.5">
          {/* Left Column - Quick Info Cards */}
          <div className="column w-80 flex flex-col gap-2.5">
            {/* Store Info Card */}
            <TableContainer>
              <div className="bg-card rounded-lg border border-border p-4">
                <h2 className="text-sm font-semibold text-foreground mb-4">Store Information</h2>
                <div className="space-y-4">
                  <InformationCard label="Domain" value={seller.domain} icon={Globe} />
                  <InformationCard label="Collection" value={seller.category || "Not specified"} icon={Tag} />
                  <InformationCard label="Niche" value={seller.niche || "Not specified"} icon={Tag} />
                  <InformationCard label="Owner ID" value={seller.owner_id?.substring(0, 12)} icon={User} />
                </div>
              </div>
            </TableContainer>

            {/* Status Card */}
            <TableContainer>
              <div className="bg-card rounded-lg border border-border p-4">
                <h2 className="text-sm font-semibold text-foreground mb-4">Status</h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground font-medium">Onboarded</p>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${seller.onboarding_completed ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                      {seller.onboarding_completed ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground font-medium">Subscription</p>
                    <p className="text-sm font-semibold text-foreground">{seller.subscription_plan?.name || "Free"}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground font-medium">System Status</p>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${seller.deleted_at ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                      {seller.deleted_at ? "Deleted" : "Active"}
                    </span>
                  </div>
                </div>
              </div>
            </TableContainer>

            {/* Dates Card */}
            <TableContainer>
              <div className="bg-card rounded-lg border border-border p-4">
                <h2 className="text-sm font-semibold text-foreground mb-4">Timeline</h2>
                <div className="space-y-4">
                  <InformationCard
                    label="Created"
                    value={seller.created_at ? format(new Date(seller.created_at), "MMM dd, yyyy") : "-"}
                    icon={Calendar}
                  />
                  <InformationCard
                    label="Updated"
                    value={seller.updated_at ? format(new Date(seller.updated_at), "MMM dd, yyyy") : "-"}
                    icon={Calendar}
                  />
                </div>
              </div>
            </TableContainer>
          </div>

          {/* Right Column - Main Content */}
          <div className="column flex-1 flex flex-col gap-2.5">
            {/* About Section */}
            {seller.about && (
              <TableContainer>
                <div className="bg-card rounded-lg border border-border p-4">
                  <h2 className="text-sm font-semibold text-foreground mb-3">About</h2>
                  <p className="text-sm text-foreground leading-relaxed">{seller.about}</p>
                </div>
              </TableContainer>
            )}

            {/* Imported Products Table */}
            {seller.imported_products && seller.imported_products.length > 0 && (
              <TableContainer>
                <div className="bg-card rounded-lg border border-border overflow-hidden">
                  <div className="border-b border-border px-4 py-3 flex items-center gap-2">
                    <Package className="h-5 w-5 text-muted-foreground" />
                    <h2 className="text-sm font-semibold text-foreground">Imported Products ({seller.imported_products.length})</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/30 border-b border-border">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Product ID</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Margin</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Payment Types</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Added</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {seller.imported_products.map((product: any, idx: number) => (
                          <tr key={idx} className="hover:bg-muted/20 transition-colors">
                            <td className="px-4 py-3">
                              <code className="text-xs text-muted-foreground bg-muted/30 px-2 py-1 rounded">{product.product_id.substring(0, 12)}...</code>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-semibold text-foreground">
                                {product.margin} {product.margin_type === "fixed" ? "₹" : "%"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {product.payment_type?.map((type: string, i: number) => (
                                  <span key={i} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">{format(new Date(product.created_at * 1000), "MMM dd")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TableContainer>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No seller found</p>
        </div>
      )}
    </div>
  );
}
